import { supabase } from '../lib/supabase'
import { isMissingAcademicSchema } from '../utils/gradeUtils'

const CLASS_SELECT = `
  id,
  name,
  period,
  status,
  legacy_level_code,
  teacher_id,
  secondary_teacher_id,
  term:terms(id, name, starts_on, ends_on, is_current),
  teacher:users!teacher_id(id, name, role),
  secondary_teacher:users!secondary_teacher_id(id, name, role),
  class_subjects(
    subject:subjects(id, name, notes)
  )
`

const slugify = (value, maxLength = 24) => {
  const slug = String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, maxLength)

  return slug || 'class'
}

const buildLevelCode = (termName, className) => {
  return `${slugify(termName, 10)}-${slugify(className, 28)}`.slice(0, 40)
}

const throwIfError = (error, fallbackMessage) => {
  if (!error) return
  const err = new Error(error.message || fallbackMessage)
  err.code = error.code
  err.cause = error
  throw err
}

const mapClass = (row) => {
  if (!row) return null

  return {
    ...row,
    subjects: (row.class_subjects || [])
      .map((item) => item.subject)
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name))
  }
}

export const academicService = {
  async isReady() {
    const { error } = await supabase.from('terms').select('id').limit(1)
    if (!error) return true
    if (isMissingAcademicSchema(error)) return false
    throwIfError(error, 'Could not check academic tables')
    return false
  },

  async listTerms() {
    const { data, error } = await supabase
      .from('terms')
      .select('*')
      .order('starts_on', { ascending: false })

    throwIfError(error, 'Could not load terms')
    return data || []
  },

  async createTerm({ name, startsOn, endsOn, isCurrent }) {
    if (isCurrent) {
      const { error: clearError } = await supabase
        .from('terms')
        .update({ is_current: false })
        .eq('is_current', true)
      throwIfError(clearError, 'Could not update current term')
    }

    const { data, error } = await supabase
      .from('terms')
      .insert([{
        name: name.trim(),
        starts_on: startsOn || null,
        ends_on: endsOn || null,
        is_current: Boolean(isCurrent)
      }])
      .select()
      .single()

    throwIfError(error, 'Could not create term')
    return data
  },

  async setCurrentTerm(termId) {
    const { error: clearError } = await supabase
      .from('terms')
      .update({ is_current: false })
      .eq('is_current', true)
    throwIfError(clearError, 'Could not clear current term')

    const { data, error } = await supabase
      .from('terms')
      .update({ is_current: true })
      .eq('id', termId)
      .select()
      .single()

    throwIfError(error, 'Could not set current term')
    return data
  },

  async listSubjects() {
    const { data, error } = await supabase
      .from('subjects')
      .select('*')
      .order('name', { ascending: true })

    throwIfError(error, 'Could not load subjects')
    return data || []
  },

  async createSubject({ name, notes }) {
    const { data, error } = await supabase
      .from('subjects')
      .insert([{ name: name.trim(), notes: notes?.trim() || null }])
      .select()
      .single()

    throwIfError(error, 'Could not create subject')
    return data
  },

  async updateSubject(subjectId, { name, notes }) {
    const trimmedName = name?.trim()
    if (!trimmedName) throw new Error('Subject name is required')

    const { data, error } = await supabase
      .from('subjects')
      .update({
        name: trimmedName,
        notes: notes?.trim() || null
      })
      .eq('id', subjectId)
      .select()
      .single()

    throwIfError(error, 'Could not update subject')
    return data
  },

  async listTeachers() {
    const { data, error } = await supabase
      .from('users')
      .select('id, name, role, code')
      .in('role', ['instructor', 'admin'])
      .order('name', { ascending: true })

    throwIfError(error, 'Could not load teachers')
    return data || []
  },

  async listClasses() {
    const { data, error } = await supabase
      .from('classes')
      .select(CLASS_SELECT)
      .order('name', { ascending: true })

    throwIfError(error, 'Could not load classes')
    return (data || []).map(mapClass)
  },

  async getClassByLevelCode(levelCode) {
    const { data, error } = await supabase
      .from('classes')
      .select(CLASS_SELECT)
      .eq('legacy_level_code', levelCode)
      .maybeSingle()

    if (error && isMissingAcademicSchema(error)) return null
    throwIfError(error, 'Could not load class')
    return mapClass(data)
  },

  async createClass({
    name,
    termId,
    teacherId,
    secondaryTeacherId,
    period,
    subjectIds
  }) {
    const trimmedName = name.trim()
    if (!trimmedName) throw new Error('Class name is required')
    if (!termId) throw new Error('Term is required')
    if (!teacherId) throw new Error('Teacher is required')

    const { data: term, error: termError } = await supabase
      .from('terms')
      .select('id, name')
      .eq('id', termId)
      .single()
    throwIfError(termError, 'Could not load term')

    const baseCode = buildLevelCode(term.name, trimmedName)
    let levelCode = baseCode
    let attempt = 1

    while (attempt <= 8) {
      const { data: existing } = await supabase
        .from('levels')
        .select('code')
        .eq('code', levelCode)
        .maybeSingle()

      if (!existing) break
      attempt += 1
      levelCode = `${baseCode.slice(0, 36)}-${attempt}`
    }

    const { error: levelError } = await supabase
      .from('levels')
      .insert([{
        code: levelCode,
        name: trimmedName,
        period: period?.trim() || null,
        active: true,
        teacher_id: teacherId,
        secondary_teacher_id: secondaryTeacherId || null
      }])
    throwIfError(levelError, 'Could not create backing class level')

    const { data: classRow, error: classError } = await supabase
      .from('classes')
      .insert([{
        name: trimmedName,
        term_id: termId,
        teacher_id: teacherId,
        secondary_teacher_id: secondaryTeacherId || null,
        period: period?.trim() || null,
        status: 'active',
        legacy_level_code: levelCode
      }])
      .select()
      .single()

    if (classError) {
      await supabase.from('levels').delete().eq('code', levelCode)
      throwIfError(classError, 'Could not create class')
    }

    if ((subjectIds || []).length) {
      const { error: subjectsError } = await supabase
        .from('class_subjects')
        .insert(subjectIds.map((subjectId) => ({
          class_id: classRow.id,
          subject_id: subjectId
        })))

      if (subjectsError) {
        await supabase.from('classes').delete().eq('id', classRow.id)
        await supabase.from('levels').delete().eq('code', levelCode)
        throwIfError(subjectsError, 'Could not attach subjects')
      }
    }

    return this.getClassByLevelCode(levelCode)
  },

  async updateClass(classId, { name, period }) {
    const trimmedName = name?.trim()
    if (!trimmedName) throw new Error('Class name is required')

    const { data: classRow, error: loadError } = await supabase
      .from('classes')
      .select('id, legacy_level_code')
      .eq('id', classId)
      .single()
    throwIfError(loadError, 'Could not load class')

    const { error: classError } = await supabase
      .from('classes')
      .update({
        name: trimmedName,
        period: period?.trim() || null
      })
      .eq('id', classId)
    throwIfError(classError, 'Could not rename class')

    const { error: levelError } = await supabase
      .from('levels')
      .update({
        name: trimmedName,
        period: period?.trim() || null
      })
      .eq('code', classRow.legacy_level_code)
    throwIfError(levelError, 'Could not rename gradebook class')

    return this.getClassByLevelCode(classRow.legacy_level_code)
  },

  async addClassSubject(classId, subjectId) {
    const { error } = await supabase
      .from('class_subjects')
      .upsert({ class_id: classId, subject_id: subjectId }, { onConflict: 'class_id,subject_id' })
    throwIfError(error, 'Could not add subject to class')
  },

  async removeClassSubject(classId, subjectId) {
    const { error } = await supabase
      .from('class_subjects')
      .delete()
      .eq('class_id', classId)
      .eq('subject_id', subjectId)
    throwIfError(error, 'Could not remove subject from class')
  },

  async listStudentsForTeacher(teacherId) {
    const classes = await this.listClasses()
    const myClassIds = classes
      .filter((classItem) => (
        String(classItem.teacher_id) === String(teacherId) ||
        String(classItem.secondary_teacher_id) === String(teacherId)
      ))
      .map((classItem) => classItem.id)

    if (!myClassIds.length) return []

    const { data, error } = await supabase
      .from('class_enrollment')
      .select('student:users(id, name, code, role)')
      .in('class_id', myClassIds)

    throwIfError(error, 'Could not load teacher students')
    const byId = new Map()
    ;(data || []).forEach((row) => {
      if (row.student?.id) byId.set(row.student.id, row.student)
    })
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
  },

  async listClassStudents(classId) {
    const { data, error } = await supabase
      .from('class_enrollment')
      .select(`
        id,
        status,
        enrolled_at,
        student:users(id, name, email, code)
      `)
      .eq('class_id', classId)
      .order('enrolled_at', { ascending: true })

    throwIfError(error, 'Could not load class roster')
    return (data || [])
      .map((row) => ({ ...row.student, enrollmentStatus: row.status, enrolledAt: row.enrolled_at }))
      .filter((student) => student?.id)
      .sort((a, b) => a.name.localeCompare(b.name))
  },

  async enrollStudents(classId, studentIds, levelCode) {
    const uniqueIds = [...new Set(studentIds.filter(Boolean))]
    if (!uniqueIds.length) return []

    const classRows = uniqueIds.map((studentId) => ({
      class_id: classId,
      student_id: studentId,
      status: 'enrolled',
      enrolled_at: new Date().toISOString()
    }))

    const { error: classError } = await supabase
      .from('class_enrollment')
      .upsert(classRows, { onConflict: 'class_id,student_id' })
    throwIfError(classError, 'Could not enroll students in class')

    const levelRows = uniqueIds.map((studentId) => ({
      student_id: studentId,
      level_code: levelCode,
      enrolled_at: new Date().toISOString()
    }))

    const { error: levelError } = await supabase
      .from('student_enrollment')
      .insert(levelRows)

    if (levelError && levelError.code !== '23505') {
      throwIfError(levelError, 'Could not enroll students in gradebook class')
    }

    return uniqueIds
  },

  async unenrollStudent(classId, studentId, levelCode) {
    const { error: classError } = await supabase
      .from('class_enrollment')
      .delete()
      .eq('class_id', classId)
      .eq('student_id', studentId)
    throwIfError(classError, 'Could not remove student from class')

    const { error: levelError } = await supabase
      .from('student_enrollment')
      .delete()
      .eq('level_code', levelCode)
      .eq('student_id', studentId)
    throwIfError(levelError, 'Could not remove student from gradebook class')
  },

  async syncEnrollmentFromLevel(studentId, levelCode, action) {
    try {
      const classRecord = await this.getClassByLevelCode(levelCode)
      if (!classRecord) return

      if (action === 'remove') {
        await supabase
          .from('class_enrollment')
          .delete()
          .eq('class_id', classRecord.id)
          .eq('student_id', studentId)
        return
      }

      await supabase
        .from('class_enrollment')
        .upsert({
          class_id: classRecord.id,
          student_id: studentId,
          status: 'enrolled',
          enrolled_at: new Date().toISOString()
        }, { onConflict: 'class_id,student_id' })
    } catch (error) {
      if (isMissingAcademicSchema(error)) return
      throw error
    }
  }
}

export default academicService
